import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { ApiError } from '../lib/api';
import { auth } from '../lib/firebase';
import { Alert, Card } from '../components/ui';
import PasswordField from '../components/PasswordField';

type AuthErrorLike = { code?: string };

/**
 * This page is reached from the link `Login.tsx` asks Firebase to send,
 * configured with `handleCodeInApp: true` — so the whole reset happens on our
 * own domain, with our own UI, rather than bouncing through Firebase's
 * generic hosted page.
 */

function friendlyError(err: unknown, fallback: string): string {
  const code = (err as AuthErrorLike | undefined)?.code;
  if (code === 'auth/expired-action-code') return 'This reset link has expired. Request a new one.';
  if (code === 'auth/invalid-action-code') {
    return 'This reset link has already been used or is invalid. Request a new one.';
  }
  if (code === 'auth/weak-password') return 'Please choose a stronger password.';
  return fallback;
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = params.get('oobCode') ?? '';

  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      setChecking(false);
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then(setEmail)
      .catch((err) => setLinkError(friendlyError(err, 'This reset link is not valid.')))
      .finally(() => setChecking(false));
  }, [oobCode]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setError(
        new ApiError(0, 'error', friendlyError(err, 'Could not reset the password.')),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-8">
      <h1 className="mb-6 text-center text-2xl font-bold">
        <span aria-hidden="true">🔥</span> FIRE Tracker
      </h1>

      <Card title="Choose a new password">
        {checking ? (
          <p className="text-sm text-ink-muted dark:text-ink-inverse-muted">Checking your link…</p>
        ) : !oobCode || linkError ? (
          <Alert tone="critical" title="This link isn't usable">
            <p>
              {linkError ?? 'The reset link is missing its code.'} Request a new one from the{' '}
              <Link to="/login" className="underline">
                sign-in page
              </Link>
              .
            </p>
          </Alert>
        ) : done ? (
          <Alert tone="good" title="Password updated">
            <p>Taking you to the sign-in page…</p>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && <Alert tone="critical">{error.message}</Alert>}
            {email && (
              <p className="text-sm text-ink-muted dark:text-ink-inverse-muted">
                Setting a new password for <span className="font-medium">{email}</span>.
              </p>
            )}

            <PasswordField
              label="New password"
              value={password}
              onChange={setPassword}
              error={error?.fieldError('password')}
            />

            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
